import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, BookOpen, CarFront, GraduationCap, LogOut, RefreshCw, Shield, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { adminApi, getApiErrorMessage } from '@/lib/api';
import { UserProfile } from '@/types';

interface AdminDashboardProps {
  currentUser: UserProfile;
  onLogout: () => void;
}

const AdminDashboard = ({ currentUser, onLogout }: AdminDashboardProps) => {
  const queryClient = useQueryClient();

  const { data, isLoading, isRefetching, refetch } = useQuery({
    queryKey: ['schoolAdminDashboard'],
    queryFn: adminApi.getSchoolDashboard,
    enabled: currentUser.role === 'admin',
  });

  const refreshDashboard = () => {
    queryClient.invalidateQueries({ queryKey: ['schoolAdminDashboard'] });
    refetch();
  };

  const deleteStudentMutation = useMutation({
    mutationFn: adminApi.deleteStudent,
    onSuccess: () => {
      toast.success('Student removed successfully.');
      refreshDashboard();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to remove student.'));
    },
  });

  const deleteRideMutation = useMutation({
    mutationFn: adminApi.deleteRide,
    onSuccess: () => {
      toast.success('Ride removed successfully.');
      refreshDashboard();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to remove ride.'));
    },
  });

  const deletePostMutation = useMutation({
    mutationFn: adminApi.deletePost,
    onSuccess: () => {
      toast.success('Post removed successfully.');
      refreshDashboard();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to remove post.'));
    },
  });

  const confirmAndRun = (message: string, action: () => void) => {
    if (window.confirm(message)) {
      action();
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="animate-pulse text-muted-foreground">Loading school admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-lg rounded-3xl border border-border bg-card p-8 text-center shadow-warm">
          <h1 className="font-display text-2xl font-bold text-foreground">Dashboard unavailable</h1>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            We could not load the school admin dashboard. Try refreshing, or sign in again if your session changed.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button variant="outline" onClick={() => refetch()}>
              Try again
            </Button>
            <Button onClick={onLogout}>Sign out</Button>
          </div>
        </div>
      </div>
    );
  }

  const statsCards = [
    { label: 'Students', value: data.stats.students, icon: Users },
    { label: 'Drivers', value: data.stats.drivers, icon: CarFront },
    { label: 'Riders', value: data.stats.riders, icon: GraduationCap },
    { label: 'Rides', value: data.stats.rides, icon: BarChart3 },
    { label: 'Active Rides', value: data.stats.activeRides, icon: Shield },
    { label: 'Posts', value: data.stats.posts, icon: BookOpen },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="absolute inset-x-0 top-0 h-72 bg-[radial-gradient(circle_at_top_left,_hsla(25,85%,55%,0.18),_transparent_42%),radial-gradient(circle_at_top_right,_hsla(140,35%,45%,0.12),_transparent_36%)]" />
      <main className="relative mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <header className="rounded-[2rem] border border-border/70 bg-card/95 p-6 shadow-warm-lg backdrop-blur">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-secondary/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-secondary-foreground">
                School Admin
              </div>
              <h1 className="mt-4 font-display text-4xl font-bold text-foreground">Administrative control center</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
                Reviewing activity for {data.school.schoolName || currentUser.schoolName || 'your school'} across students, rides, and community posts.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => refetch()} disabled={isRefetching}>
                <RefreshCw className={`mr-2 h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" onClick={onLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </Button>
            </div>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-2xl border border-border/70 bg-background/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">School Name</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{data.school.schoolName || 'Unassigned'}</p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-background/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">School Domain</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{data.school.schoolDomain || 'Unknown'}</p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-background/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Signed In As</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{currentUser.email}</p>
            </div>
          </div>
        </header>

        <section className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {statsCards.map(({ label, value, icon: Icon }) => (
            <div key={label} className="rounded-3xl border border-border/70 bg-card/90 p-5 shadow-warm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{label}</p>
                  <p className="mt-3 font-display text-3xl font-bold text-foreground">{value}</p>
                </div>
                <div className="rounded-2xl bg-primary/10 p-3 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
              </div>
            </div>
          ))}
        </section>

        <section className="mt-8 grid gap-8 xl:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-8">
            <div className="rounded-[2rem] border border-border/70 bg-card/95 p-6 shadow-warm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl font-bold text-foreground">Students</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Domain-filtered student accounts for this school.</p>
                </div>
                <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
                  {data.students.length} records
                </span>
              </div>

              <div className="mt-5 space-y-3">
                {data.students.length === 0 && (
                  <p className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                    No students are currently available for moderation.
                  </p>
                )}

                {data.students.map((student) => (
                  <div key={student.id} className="flex flex-col gap-4 rounded-2xl border border-border/70 bg-background/70 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-semibold text-foreground">{student.name}</p>
                      <p className="text-sm text-muted-foreground">{student.email}</p>
                      <p className="mt-1 text-xs uppercase tracking-[0.16em] text-muted-foreground">{student.role}</p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => confirmAndRun(`Delete student ${student.email}?`, () => deleteStudentMutation.mutate(student.id))}
                      disabled={deleteStudentMutation.isPending}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-border/70 bg-card/95 p-6 shadow-warm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl font-bold text-foreground">Rides</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Current and historical rides scoped to this school.</p>
                </div>
                <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
                  {data.rides.length} records
                </span>
              </div>

              <div className="mt-5 space-y-3">
                {data.rides.length === 0 && (
                  <p className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                    No rides are available right now.
                  </p>
                )}

                {data.rides.map((ride) => (
                  <div key={ride.id} className="flex flex-col gap-4 rounded-2xl border border-border/70 bg-background/70 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="font-semibold text-foreground">{ride.destinationName || 'Untitled ride'}</p>
                        <p className="text-sm text-muted-foreground">
                          Driver: {ride.driver.name || 'Unknown'} • {ride.date} • {ride.startTime} - {ride.endTime}
                        </p>
                        <p className="mt-1 text-xs uppercase tracking-[0.16em] text-muted-foreground">
                          Status: {ride.tripStatusDriver || ride.tripStatus || ride.status || 'pending'}
                        </p>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => confirmAndRun(`Delete ride "${ride.destinationName}"?`, () => deleteRideMutation.mutate(ride.id))}
                        disabled={deleteRideMutation.isPending}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-[2rem] border border-border/70 bg-card/95 p-6 shadow-warm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="font-display text-2xl font-bold text-foreground">Community Posts</h2>
                <p className="mt-1 text-sm text-muted-foreground">Posts published inside this school community.</p>
              </div>
              <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
                {data.posts.length} records
              </span>
            </div>

            <div className="mt-5 space-y-3">
              {data.posts.length === 0 && (
                <p className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                  No posts are available for moderation.
                </p>
              )}

              {data.posts.map((post) => (
                <div key={post.id} className="rounded-2xl border border-border/70 bg-background/70 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-semibold text-foreground">{post.title || 'Untitled post'}</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        By {post.author?.name || 'Unknown'} • {post.type}
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => confirmAndRun(`Delete post "${post.title}"?`, () => deletePostMutation.mutate(post.id))}
                      disabled={deletePostMutation.isPending}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Remove
                    </Button>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-muted-foreground">{post.description || 'No description provided.'}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminDashboard;
